import { ApiError } from './http';
import { ApiErrorCodes } from './errors';
import type { ApiErrorCodeLike } from './errors';
import type { NextcloudApi } from './contract';
import type {
  NextcloudCapabilities,
  NextcloudEntry,
  NextcloudFinishInput,
  NextcloudLinkReason,
  NextcloudPending,
  NextcloudSaveEvent,
  NextcloudSettings,
  NextcloudShared,
  NextcloudStatus,
  NextcloudUpload,
  StagedAttachment,
} from './types';

/**
 * MOCK: Nextcloud (plan 18, ADR 0084), for `VITE_USE_MOCKS=true` and tests.
 *
 * Every route of plan 18 §5, following the real server's rules rather than
 * being kinder than it (the HANDOFF lesson): not linked until a Login Flow is
 * granted or an app password is typed; the file and upload routes answer
 * `409 nextcloud_not_linked` until then; the app password is write-only
 * (nothing here ever returns it); settings are clamped to the instance's
 * capabilities; a chunk must be exactly the size its number says; a link's
 * password is answered once and never again.
 *
 * The flow grants itself after `autoGrantAfterPolls` polls (the dev server
 * sets 2, so a person can click through), and only by
 * {@link mockNextcloudGrant} when that is left unset (tests). Addresses the
 * mock understands, so every refusal can be seen:
 *   - not `https://`, or with a user, query or fragment → `nextcloud_url_invalid`;
 *   - `localhost`, `*.internal` or a private IPv4 address → `nextcloud_host_blocked`;
 *   - a host starting `unreachable` → `nextcloud_unreachable`;
 *   - an app password containing `invalid` → `nextcloud_rejected`.
 */

const MIB = 1024 * 1024;
const GIB = 1024 * MIB;
const DAY_MS = 86_400_000;
/** A Login Flow v2 token lives 20 minutes on Nextcloud. */
const FLOW_TTL_MS = 20 * 60_000;
const PAGE_SIZE = 50;
const PASSWORD_LENGTH = 14;

/** The linked instance when the flow does not say otherwise. */
export const MOCK_NEXTCLOUD_BASE_URL = 'https://cloud.example.test';

/** The stored settings; the save folder is derived per read (`status()`). */
type StoredSettings = Omit<NextcloudSettings, 'saveFolder' | 'saveFolderDefault'>;

const DEFAULT_SETTINGS: StoredSettings = {
  linkThresholdBytes: 10 * MIB,
  shareExpiryDays: 30,
  uploadFolder: 'Mail attachments',
  askPassword: false,
};

const LIMITS = {
  maxUploadBytes: 2 * GIB,
  chunkBytes: 8 * MIB,
  minThresholdBytes: 1 * MIB,
  maxThresholdBytes: 24 * MIB,
};

/** The attachment staging store's per-file cap, as `GET /accounts` reports it. */
const MAX_STAGE_BYTES = 24 * MIB;

const DEFAULT_CAPABILITIES: NextcloudCapabilities = {
  version: 33,
  publicLinks: true,
  passwordEnforced: false,
  passwordOptional: true,
  expiryEnforced: false,
  expiryMaxDays: 0,
  chunkMaxBytes: 100 * MIB,
};

function ncError(
  status: number,
  code: ApiErrorCodeLike,
  message: string,
  details?: Record<string, unknown>,
): ApiError {
  return new ApiError(status, {
    error: { code, message, ...(details ? { details } : {}), request_id: 'mock-nextcloud' },
  });
}

function abortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError');
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(abortError());
    }, { once: true });
  });
}

/** `n` characters of `alphabet`, from the platform's CSPRNG when there is one. */
function randomString(n: number, alphabet: string): string {
  const bytes = new Uint8Array(n);
  if (globalThis.crypto?.getRandomValues !== undefined) globalThis.crypto.getRandomValues(bytes);
  else for (let i = 0; i < n; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return [...bytes].map((b) => alphabet[b % alphabet.length]).join('');
}

const TOKEN_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
// No 0/O, 1/l/I: the sender reads this one out to someone.
const PASSWORD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

/** The server's URL normalisation, in its simplest form. */
function normaliseBaseUrl(raw: string): string {
  const typed = raw.trim();
  let url: URL;
  try {
    url = new URL(typed);
  } catch {
    throw ncError(400, ApiErrorCodes.NextcloudURLInvalid, 'That is not a web address. Enter the address you open Nextcloud at.');
  }
  if (url.protocol !== 'https:') {
    throw ncError(400, ApiErrorCodes.NextcloudURLInvalid, 'The address must start with https://.');
  }
  if (url.username !== '' || url.password !== '' || url.search !== '' || url.hash !== '') {
    throw ncError(400, ApiErrorCodes.NextcloudURLInvalid, 'Enter only the address of your Nextcloud, without a name, a query or a #.');
  }
  const host = url.hostname;
  if (
    host === 'localhost' ||
    host.endsWith('.internal') ||
    /^(10\.|127\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host)
  ) {
    throw ncError(400, ApiErrorCodes.NextcloudHostBlocked, `${host} cannot be linked from this server.`, {
      reason: 'private_address',
    });
  }
  if (host.startsWith('unreachable')) {
    throw ncError(502, ApiErrorCodes.NextcloudUnreachable, `${host} did not answer. Check the address and try again.`);
  }
  return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
}

/** `a/b/` and `/a/b` are both `a/b`; `..` is refused, as the server does (D18). */
function normalisePath(raw: string): string {
  const parts = raw.split('/').filter((p) => p !== '' && p !== '.');
  if (parts.includes('..')) {
    throw ncError(400, ApiErrorCodes.BadRequest, 'That path is not allowed.', { field: 'path' });
  }
  return parts.join('/');
}

function parentOf(path: string): string {
  const at = path.lastIndexOf('/');
  return at === -1 ? '' : path.slice(0, at);
}

function nameOf(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

/** `report.pdf` → `report (2).pdf`, as the server names a collision (D11). */
function withSuffix(name: string, n: number): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? `${name.slice(0, dot)} (${n})${name.slice(dot)}` : `${name} (${n})`;
}

function contentTypeOf(name: string): string {
  const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  const types: Record<string, string> = {
    pdf: 'application/pdf',
    zip: 'application/zip',
    txt: 'text/plain',
    jpg: 'image/jpeg',
    png: 'image/png',
    mp4: 'video/mp4',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  };
  return types[ext] ?? 'application/octet-stream';
}

interface MockNode {
  isDir: boolean;
  sizeBytes: number;
  contentType?: string;
  modifiedAt: string;
  fileId: number;
}

/**
 * MOCK: the person's files. Folders and files of every size the picker has
 * to handle: small ones to attach as a copy, and ones over the 24 MiB
 * attachment cap that can only go as a link.
 */
function seedTree(): Map<string, MockNode> {
  const tree = new Map<string, MockNode>();
  let fileId = 1000;
  const put = (path: string, sizeBytes: number | 'dir', modifiedAt: string) => {
    fileId += 1;
    tree.set(path, {
      isDir: sizeBytes === 'dir',
      sizeBytes: sizeBytes === 'dir' ? 0 : sizeBytes,
      ...(sizeBytes === 'dir' ? {} : { contentType: contentTypeOf(path) }),
      modifiedAt,
      fileId,
    });
  };
  put('', 'dir', '2026-09-20T08:00:00Z');
  put('Documents', 'dir', '2026-09-18T14:02:00Z');
  put('Documents/Quarterly report.pdf', 2_516_582, '2026-09-18T14:02:00Z');
  put('Documents/Budget 2026.xlsx', 389_120, '2026-08-30T09:41:00Z');
  put('Documents/Contracts', 'dir', '2026-07-02T11:15:00Z');
  put('Documents/Contracts/Lease agreement.pdf', 1_153_433, '2026-07-02T11:15:00Z');
  put('Photos', 'dir', '2026-09-12T19:30:00Z');
  put('Photos/Beach.jpg', 4_404_019, '2026-08-14T17:05:00Z');
  put('Photos/Holiday video.mp4', 509_607_936, '2026-09-12T19:30:00Z');
  put('Mail attachments', 'dir', '2026-09-01T10:00:00Z');
  put('Site plans.zip', 32_505_856, '2026-09-10T16:48:00Z');
  put('Notes.txt', 2_048, '2026-09-19T07:12:00Z');
  return tree;
}

interface MockLink {
  state: NextcloudStatus['state'];
  reason?: NextcloudLinkReason;
  reasonAt?: string;
  remoteRevoked?: boolean;
  baseUrl?: string;
  loginName?: string;
  linkedAt?: string;
  lastUsedAt?: string;
  lastVerifiedAt?: string;
}

interface MockFlow extends NextcloudPending {
  baseUrl: string;
  polls: number;
  granted: boolean;
}

interface MockUploadRow extends NextcloudUpload {
  /** Chunk numbers received, 1-based. */
  received: Set<number>;
  chunkBytes: number;
  /** When an open draft last touched it (D9). */
  touchedAt: string;
}

/** What the mock needs to know about a received message's part to "save" it. */
export interface MockSavablePart {
  filename: string;
  sizeBytes: number;
  contentType: string;
}

export interface MockNextcloudOptions {
  /**
   * A part of a message in the mock mailbox, for *Save to Nextcloud* (ADR
   * 0086). Undefined: the message or the part is not there.
   */
  findPart?: (accountId: string, messageId: string, partId: string) => MockSavablePart | undefined;
  /** The mock's per-call latency (0 in tests). */
  delay: (signal?: AbortSignal) => Promise<void>;
  /**
   * Polls answered "pending" before the flow grants itself. Unset: only
   * {@link MockNextcloudControls.grant} grants (tests).
   */
  autoGrantAfterPolls?: number;
  /** Pause between a chunk's progress reports; 0 reports it done at once. */
  progressDelayMs: number;
  /** The mock's clock. */
  now?: () => Date;
}

/** Test and devtools handles on the mock's Nextcloud. */
export interface MockNextcloudControls {
  /** The person pressed *Grant access* on the pending flow. */
  grant(): void;
  /** The pending flow's 20 minutes are up: the next poll answers 410. */
  expirePending(): void;
  /** `false` answers every route with `501 unsupported_by_host`, as a deployment without the feature. */
  setAvailable(available: boolean): void;
  /**
   * The next PUT of chunk `n` (of any chunk, when `n` is left out) fails
   * with this code (`nextcloud_unreachable`, 502).
   */
  failNextChunk(code?: ApiErrorCodeLike, n?: number): void;
  /** Nextcloud answers 401 from now on: the next call withdraws the link (`rejected`). */
  revokeRemotely(): void;
  /** Overrides the instance's sharing policy (enforced password, enforced expiry…). */
  setCapabilities(patch: Partial<NextcloudCapabilities>): void;
  /** Fills the Nextcloud up until this much is free. */
  setFreeBytes(bytes: number): void;
  /** Every upload row, including the ones a list hides. */
  uploads(): NextcloudUpload[];
  /** Every file and folder path on the mock Nextcloud (tests). */
  paths(): string[];
  /** Send's check: every id is the owner's (404 otherwise). */
  requireUploads(ids: readonly string[]): void;
  /** Send and Send later: `staged` rows become `sent` (D13); other states are left alone. */
  markSent(ids: readonly string[], accountId: string, subject: string): void;
}

/** The most recently created mock, for the module-level hooks below. */
let current: MockNextcloudControls | undefined;

/** MOCK: grants the pending Login Flow of the current mock (tests, devtools). */
export function mockNextcloudGrant(): void {
  current?.grant();
}

/** MOCK: the current mock's next PUT of chunk `n` (of any chunk) fails with `code`. */
export function mockNextcloudFailNextChunk(
  code: ApiErrorCodeLike = ApiErrorCodes.NextcloudUnreachable,
  n?: number,
): void {
  current?.failNextChunk(code, n);
}

/** MOCK: turns the current mock's feature off (501) or back on. */
export function mockNextcloudSetAvailable(available: boolean): void {
  current?.setAvailable(available);
}

/** MOCK: the controls of the current mock. */
export function mockNextcloudControls(): MockNextcloudControls | undefined {
  return current;
}

export function createMockNextcloud({
  findPart,
  delay,
  autoGrantAfterPolls,
  progressDelayMs,
  now = () => new Date(),
}: MockNextcloudOptions): { api: NextcloudApi; controls: MockNextcloudControls } {
  let available = true;
  let rejected = false;
  let failChunk: { code: ApiErrorCodeLike; n: number | undefined } | undefined;
  let link: MockLink = { state: 'none' };
  let flow: MockFlow | undefined;
  let settings: StoredSettings = { ...DEFAULT_SETTINGS };
  /** The folder the last save went to, when it was not the default (ADR 0086). */
  let saveFolder: string | undefined;
  let capabilities: NextcloudCapabilities = { ...DEFAULT_CAPABILITIES };
  const quota = { totalBytes: 100 * GIB, usedBytes: 37 * GIB + 512 * MIB };
  const tree = seedTree();
  let uploads: MockUploadRow[] = [];
  let seq = 0;
  let fileIds = 5000;

  const iso = (): string => now().toISOString();
  const nextId = (prefix: string): string => {
    seq += 1;
    return `${prefix}-${String(seq).padStart(5, '0')}`;
  };

  const status = (): NextcloudStatus => {
    const active = link.state === 'active';
    const host = link.baseUrl !== undefined ? new URL(link.baseUrl).host : undefined;
    return {
      state: link.state,
      ...(link.reason !== undefined ? { reason: link.reason } : {}),
      ...(link.reasonAt !== undefined ? { reasonAt: link.reasonAt } : {}),
      ...(link.remoteRevoked !== undefined ? { remoteRevoked: link.remoteRevoked } : {}),
      ...(active && link.baseUrl !== undefined && host !== undefined
        ? {
            baseUrl: link.baseUrl,
            host,
            loginName: link.loginName ?? 'alex',
            userId: 'alex',
            displayName: 'Alex Rendel',
          }
        : {}),
      ...(active && link.linkedAt !== undefined ? { linkedAt: link.linkedAt } : {}),
      ...(active && link.lastUsedAt !== undefined ? { lastUsedAt: link.lastUsedAt } : {}),
      ...(active && link.lastVerifiedAt !== undefined ? { lastVerifiedAt: link.lastVerifiedAt } : {}),
      ...(active
        ? {
            quota: {
              freeBytes: quota.totalBytes - quota.usedBytes,
              usedBytes: quota.usedBytes,
              totalBytes: quota.totalBytes,
              unlimited: false,
              at: iso(),
            },
            capabilities: { ...capabilities },
          }
        : {}),
      settings: { ...settings, saveFolder: saveFolder ?? saveFolderDefault(), saveFolderDefault: saveFolderDefault() },
      limits: { ...LIMITS },
      ...(flow !== undefined && Date.parse(flow.expiresAt) > now().getTime()
        ? { pending: { linkId: flow.linkId, loginUrl: flow.loginUrl, host: flow.host, expiresAt: flow.expiresAt } }
        : {}),
      suggestedUrl: MOCK_NEXTCLOUD_BASE_URL,
    };
  };

  /** `<upload folder>/Received/<yyyy-mm>`, this month (UTC, as the server). */
  const saveFolderDefault = (): string => `${settings.uploadFolder}/Received/${iso().slice(0, 7)}`;

  /** MKCOL every missing folder on the way down, as `MkdirAll` does. */
  const mkdirAll = (path: string): void => {
    const parts = path === '' ? [] : path.split('/');
    for (let i = 1; i <= parts.length; i += 1) {
      const dir = parts.slice(0, i).join('/');
      const node = tree.get(dir);
      if (node === undefined) {
        fileIds += 1;
        tree.set(dir, { isDir: true, sizeBytes: 0, modifiedAt: iso(), fileId: fileIds });
      } else if (!node.isDir) {
        throw ncError(409, ApiErrorCodes.Conflict, `${nameOf(dir)} is a file, not a folder.`, { field: 'path' });
      }
    }
  };

  const withdraw = (reason: NextcloudLinkReason): void => {
    link = { state: 'withdrawn', reason, reasonAt: iso(), ...(link.baseUrl !== undefined ? { baseUrl: link.baseUrl } : {}) };
  };

  const activate = (baseUrl: string, loginName: string): void => {
    const at = iso();
    link = { state: 'active', baseUrl, loginName, linkedAt: at, lastUsedAt: at, lastVerifiedAt: at };
    rejected = false;
    flow = undefined;
  };

  /** Every route's first check: 501 without the feature. */
  const gate = async (signal?: AbortSignal): Promise<void> => {
    await delay(signal);
    if (!available) {
      throw ncError(501, ApiErrorCodes.UnsupportedByHost, 'Nextcloud is not available on this server.');
    }
  };

  /** The file and upload routes: an active link, which Nextcloud still accepts. */
  const requireLinked = (): void => {
    if (link.state !== 'active') {
      throw ncError(409, ApiErrorCodes.NextcloudNotLinked, 'Link a Nextcloud account in Settings first.', {
        reason: link.state,
        ...(link.reason !== undefined ? { withdrawn_reason: link.reason } : {}),
      });
    }
    if (rejected) {
      // Never retried (D3): Nextcloud's brute-force guard counts our one IP.
      withdraw('rejected');
      throw ncError(409, ApiErrorCodes.NextcloudRejected, 'Nextcloud no longer accepts the link. Link it again in Settings.');
    }
    link.lastUsedAt = iso();
  };

  const requireRow = (uploadId: string): MockUploadRow => {
    const row = uploads.find((u) => u.id === uploadId && u.state !== 'deleted');
    if (row === undefined) throw ncError(404, ApiErrorCodes.NotFound, 'There is no such upload.');
    return row;
  };

  const requireNode = (path: string): MockNode => {
    const node = tree.get(path);
    if (node === undefined) {
      throw ncError(404, ApiErrorCodes.NextcloudNotFound, 'That file is no longer on your Nextcloud.');
    }
    return node;
  };

  /** The row as the wire carries it, `expired` once its link has lapsed. */
  const wire = (row: MockUploadRow): NextcloudUpload => {
    const { received: _received, chunkBytes: _chunkBytes, touchedAt: _touchedAt, ...upload } = row;
    const lapsed =
      (row.state === 'staged' || row.state === 'sent') &&
      row.expiresAt !== null &&
      Date.parse(row.expiresAt) <= now().getTime();
    return { ...upload, state: lapsed ? 'expired' : row.state };
  };

  /** Expiry and password by the settings and the instance's policy (D11). */
  const share = (row: MockUploadRow, input: NextcloudFinishInput): NextcloudShared => {
    if (!capabilities.publicLinks) {
      throw ncError(409, ApiErrorCodes.NextcloudSharingDisabled,
        'Sharing by link is turned off on your Nextcloud. Its administrator can turn it on.');
    }
    let days = input.expiryDays ?? settings.shareExpiryDays;
    if (!Number.isInteger(days) || days < 0 || days > 365) {
      throw ncError(400, ApiErrorCodes.BadRequest, 'Choose between 0 and 365 days.', { field: 'expiry_days' });
    }
    if (capabilities.expiryEnforced && (days === 0 || days > capabilities.expiryMaxDays)) days = capabilities.expiryMaxDays;
    const password =
      input.password === 'generate' || capabilities.passwordEnforced
        ? randomString(PASSWORD_LENGTH, PASSWORD_ALPHABET)
        : undefined;
    // As Nextcloud 33 answers it (measured live): the END of the last day,
    // in UTC -- a link that expires on Oct 25 says 2026-10-25T23:59:59Z.
    const expiresAt =
      days === 0 ? null : `${new Date(now().getTime() + days * DAY_MS).toISOString().slice(0, 10)}T23:59:59Z`;
    row.shareId = String(70 + seq);
    row.shareUrl = `${link.baseUrl ?? MOCK_NEXTCLOUD_BASE_URL}/s/${randomString(15, TOKEN_ALPHABET)}`;
    row.expiresAt = expiresAt;
    row.passwordSet = password !== undefined;
    row.state = 'staged';
    row.touchedAt = iso();
    return {
      upload: wire(row),
      shareUrl: row.shareUrl,
      expiresAt,
      ...(password !== undefined ? { password } : {}),
    };
  };

  const newRow = (fields: Pick<NextcloudUpload, 'origin' | 'filename' | 'sizeBytes' | 'contentType' | 'path'>): MockUploadRow => ({
    id: nextId('ncu'),
    state: 'uploading',
    fileId: null,
    shareId: null,
    shareUrl: null,
    expiresAt: null,
    passwordSet: false,
    accountId: null,
    rfcMessageId: null,
    subject: null,
    createdAt: iso(),
    sentAt: null,
    lastError: null,
    chunksDone: 0,
    chunkCount: 0,
    received: new Set<number>(),
    chunkBytes: LIMITS.chunkBytes,
    touchedAt: iso(),
    ...fields,
  });

  /** Removes a file we uploaded, and the share of any row. */
  const removeRemote = (row: MockUploadRow): void => {
    if (row.origin === 'uploaded' && row.fileId !== null) {
      const node = tree.get(row.path);
      if (node !== undefined) {
        tree.delete(row.path);
        quota.usedBytes -= node.sizeBytes;
      }
    }
    row.shareId = null;
  };

  const controls: MockNextcloudControls = {
    grant() {
      if (flow !== undefined) flow.granted = true;
    },
    expirePending() {
      if (flow !== undefined) flow.expiresAt = new Date(now().getTime() - 1000).toISOString();
    },
    setAvailable(next) {
      available = next;
    },
    failNextChunk(code = ApiErrorCodes.NextcloudUnreachable, n) {
      failChunk = { code, n };
    },
    revokeRemotely() {
      rejected = true;
    },
    setCapabilities(patch) {
      capabilities = { ...capabilities, ...patch };
    },
    setFreeBytes(bytes) {
      quota.usedBytes = quota.totalBytes - bytes;
    },
    uploads() {
      return uploads.map(wire);
    },
    paths() {
      return [...tree.keys()].filter((p) => p !== '');
    },
    requireUploads(ids) {
      for (const uploadId of ids) {
        if (!uploads.some((u) => u.id === uploadId)) {
          throw ncError(404, ApiErrorCodes.NotFound, 'A Nextcloud link in this message is no longer known.', {
            field: 'nextcloud_upload_ids',
          });
        }
      }
    },
    markSent(ids, accountId, subject) {
      for (const row of uploads) {
        if (!ids.includes(row.id) || row.state !== 'staged') continue;
        row.state = 'sent';
        row.sentAt = iso();
        row.accountId = accountId;
        row.subject = subject;
        row.rfcMessageId = `<${row.id}@mock.example>`;
      }
    },
  };
  current = controls;

  const api: NextcloudApi = {
    async getNextcloud(signal) {
      await gate(signal);
      return status();
    },

    async startNextcloudLink(baseUrl) {
      await gate();
      const normalised = normaliseBaseUrl(baseUrl);
      const token = randomString(32, TOKEN_ALPHABET);
      flow = {
        linkId: nextId('nclink'),
        loginUrl: `${normalised}/index.php/login/v2/flow/${token}`,
        host: new URL(normalised).host,
        expiresAt: new Date(now().getTime() + FLOW_TTL_MS).toISOString(),
        baseUrl: normalised,
        polls: 0,
        granted: false,
      };
      return { linkId: flow.linkId, loginUrl: flow.loginUrl, host: flow.host, expiresAt: flow.expiresAt };
    },

    async pollNextcloudLink(linkId, signal) {
      await gate(signal);
      if (flow === undefined || flow.linkId !== linkId) {
        throw ncError(404, ApiErrorCodes.NotFound, 'That link attempt is over. Start again.');
      }
      if (Date.parse(flow.expiresAt) <= now().getTime()) {
        flow = undefined;
        throw ncError(410, ApiErrorCodes.NextcloudLinkExpired,
          'The sign-in window was open for more than 20 minutes. Start again.');
      }
      flow.polls += 1;
      if (autoGrantAfterPolls !== undefined && flow.polls > autoGrantAfterPolls) flow.granted = true;
      if (!flow.granted) return { pending: { state: 'pending', expiresAt: flow.expiresAt } };
      activate(flow.baseUrl, 'alex');
      return { linked: status() };
    },

    async cancelNextcloudLink(linkId) {
      await gate();
      if (flow?.linkId === linkId) flow = undefined;
    },

    async putNextcloudCredential(input) {
      await gate();
      const normalised = normaliseBaseUrl(input.baseUrl);
      if (input.loginName.trim() === '') {
        throw ncError(400, ApiErrorCodes.BadRequest, 'Enter your Nextcloud login name.', { field: 'login_name' });
      }
      if (input.appPassword.trim() === '') {
        throw ncError(400, ApiErrorCodes.BadRequest, 'Enter the app password.', { field: 'app_password' });
      }
      if (input.appPassword.includes('invalid')) {
        throw ncError(409, ApiErrorCodes.NextcloudRejected,
          'Nextcloud did not accept that login name and app password.');
      }
      activate(normalised, input.loginName.trim());
      return status();
    },

    async verifyNextcloud() {
      await gate();
      if (link.state !== 'active') {
        throw ncError(409, ApiErrorCodes.NextcloudNotLinked, 'There is no Nextcloud link to check.', { reason: link.state });
      }
      requireLinked();
      link.lastVerifiedAt = iso();
      return status();
    },

    async unlinkNextcloud() {
      await gate();
      const wasLinked = link.state !== 'none';
      link = wasLinked
        ? { state: 'none', reason: 'unlinked', reasonAt: iso(), remoteRevoked: !rejected }
        : { state: 'none' };
      rejected = false;
      flow = undefined;
      settings = { ...DEFAULT_SETTINGS };
      return {
        status: status(),
        message:
          'Nextcloud is unlinked. Files and links already shared from mail stay on your Nextcloud as they are.',
      };
    },

    async putNextcloudSettings(input) {
      await gate();
      requireLinked();
      const next = { ...settings };
      if (input.linkThresholdBytes !== undefined) {
        if (!Number.isInteger(input.linkThresholdBytes) || input.linkThresholdBytes <= 0) {
          throw ncError(400, ApiErrorCodes.BadRequest, 'Choose a size.', { field: 'link_threshold_bytes' });
        }
        next.linkThresholdBytes = Math.min(
          Math.max(input.linkThresholdBytes, LIMITS.minThresholdBytes),
          LIMITS.maxThresholdBytes,
        );
      }
      if (input.shareExpiryDays !== undefined) {
        if (!Number.isInteger(input.shareExpiryDays) || input.shareExpiryDays < 0 || input.shareExpiryDays > 365) {
          throw ncError(400, ApiErrorCodes.BadRequest, 'Choose between 0 (never) and 365 days.', { field: 'share_expiry_days' });
        }
        next.shareExpiryDays = input.shareExpiryDays;
      }
      if (input.uploadFolder !== undefined) {
        const folder = normalisePath(input.uploadFolder);
        if (folder === '') {
          throw ncError(400, ApiErrorCodes.BadRequest, 'Name a folder.', { field: 'upload_folder' });
        }
        next.uploadFolder = folder;
      }
      if (input.askPassword !== undefined) next.askPassword = input.askPassword;
      // Clamped to the instance's policy, as the server does (D11).
      if (capabilities.expiryEnforced && (next.shareExpiryDays === 0 || next.shareExpiryDays > capabilities.expiryMaxDays)) {
        next.shareExpiryDays = capabilities.expiryMaxDays;
      }
      settings = next;
      return status();
    },

    async listNextcloudFiles(path, signal) {
      await gate(signal);
      requireLinked();
      const folder = normalisePath(path);
      const node = requireNode(folder);
      if (!node.isDir) throw ncError(400, ApiErrorCodes.BadRequest, 'That is a file, not a folder.', { field: 'path' });
      const sharedPaths = new Set(
        uploads.filter((u) => u.shareId !== null && u.state !== 'deleted').map((u) => u.path),
      );
      const entries: NextcloudEntry[] = [...tree.entries()]
        .filter(([p]) => p !== '' && parentOf(p) === folder)
        .map(([p, n]) => ({
          name: nameOf(p),
          path: p,
          isDir: n.isDir,
          sizeBytes: n.sizeBytes,
          ...(n.contentType !== undefined ? { contentType: n.contentType } : {}),
          modifiedAt: n.modifiedAt,
          fileId: n.fileId,
          shared: sharedPaths.has(p),
        }))
        // Folders first, then by name: the picker's order.
        .sort((a, b) => Number(b.isDir) - Number(a.isDir) || a.name.localeCompare(b.name));
      return { path: folder, entries, quotaAvailableBytes: quota.totalBytes - quota.usedBytes };
    },

    async stageNextcloudFile(path) {
      await gate();
      requireLinked();
      const file = normalisePath(path);
      const node = requireNode(file);
      if (node.isDir) throw ncError(400, ApiErrorCodes.BadRequest, 'A folder cannot be attached.', { field: 'path' });
      if (node.sizeBytes > MAX_STAGE_BYTES) {
        throw ncError(413, ApiErrorCodes.MessageTooLarge, 'That file is too large to attach. Share it as a link instead.', {
          field: 'path',
          reason: 'file_too_large',
          max_bytes: MAX_STAGE_BYTES,
        });
      }
      const staged: StagedAttachment = {
        id: nextId('att-nc'),
        filename: nameOf(file),
        contentType: node.contentType ?? 'application/octet-stream',
        sizeBytes: node.sizeBytes,
        inline: false,
      };
      return staged;
    },

    async shareNextcloudFile(input) {
      await gate();
      requireLinked();
      const file = normalisePath(input.path);
      const node = requireNode(file);
      if (node.isDir) throw ncError(400, ApiErrorCodes.BadRequest, 'Share a file, not a folder.', { field: 'path' });
      // Our own earlier link to the same file is reused (D14); its password,
      // if it had one, was answered the first time and is not answered again.
      const origin = input.savedFrom !== undefined ? 'saved' : 'existing';
      const earlier = uploads.find(
        (u) => u.origin === origin && u.path === file && (u.state === 'staged' || u.state === 'sent'),
      );
      if (earlier?.shareUrl != null && wire(earlier).state !== 'expired') {
        earlier.touchedAt = iso();
        return { upload: wire(earlier), shareUrl: earlier.shareUrl, expiresAt: earlier.expiresAt };
      }
      const row = newRow({
        origin,
        filename: nameOf(file),
        sizeBytes: node.sizeBytes,
        contentType: node.contentType ?? 'application/octet-stream',
        path: file,
      });
      row.fileId = node.fileId;
      const shared = share(row, input);
      if (input.savedFrom !== undefined) {
        // Handed out by the person, not carried by a draft: never an orphan.
        row.state = 'sent';
        row.sentAt = iso();
        row.accountId = input.savedFrom.accountId;
        row.subject = input.savedFrom.subject;
        row.rfcMessageId = input.savedFrom.rfcMessageId.replace(/^<|>$/g, '') || null;
        shared.upload = wire(row);
      }
      uploads.push(row);
      return shared;
    },

    async reserveNextcloudUpload(input) {
      await gate();
      requireLinked();
      const filename = input.filename.trim().replace(/[/\\]/g, '_');
      if (filename === '' || filename === '.' || filename === '..') {
        throw ncError(400, ApiErrorCodes.BadRequest, 'The file has no name.', { field: 'filename' });
      }
      if (!Number.isInteger(input.sizeBytes) || input.sizeBytes < 0) {
        throw ncError(400, ApiErrorCodes.BadRequest, 'The file size is not valid.', { field: 'size_bytes' });
      }
      if (input.sizeBytes > LIMITS.maxUploadBytes) {
        throw ncError(413, ApiErrorCodes.NextcloudUploadTooLarge, 'That file is larger than this server uploads to Nextcloud.', {
          max_bytes: LIMITS.maxUploadBytes,
        });
      }
      const free = quota.totalBytes - quota.usedBytes;
      if (input.sizeBytes > free) {
        throw ncError(413, ApiErrorCodes.NextcloudQuotaExceeded, 'Your Nextcloud does not have room for that file.', {
          free_bytes: free,
        });
      }
      const month = iso().slice(0, 7);
      const folder = `${settings.uploadFolder}/${month}`;
      // MKCOL, 405 = exists (D11).
      for (const dir of [settings.uploadFolder, folder]) {
        if (!tree.has(dir)) {
          fileIds += 1;
          tree.set(dir, { isDir: true, sizeBytes: 0, modifiedAt: iso(), fileId: fileIds });
        }
      }
      const taken = (p: string) => tree.has(p) || uploads.some((u) => u.path === p && u.state === 'uploading');
      let name = filename;
      for (let n = 2; taken(`${folder}/${name}`); n += 1) name = withSuffix(filename, n);
      const row = newRow({
        origin: 'uploaded',
        filename: name,
        sizeBytes: input.sizeBytes,
        contentType: input.contentType ?? contentTypeOf(name),
        path: `${folder}/${name}`,
      });
      row.chunkCount = Math.max(1, Math.ceil(input.sizeBytes / LIMITS.chunkBytes));
      uploads.push(row);
      return { upload: wire(row), chunkBytes: row.chunkBytes, chunkCount: row.chunkCount, destinationPath: row.path };
    },

    async putNextcloudChunk(uploadId, n, chunk, options) {
      await gate(options?.signal);
      requireLinked();
      const row = requireRow(uploadId);
      if (row.state !== 'uploading') {
        throw ncError(409, ApiErrorCodes.Conflict, 'That upload is not in progress.');
      }
      if (!Number.isInteger(n) || n < 1 || n > row.chunkCount) {
        throw ncError(400, ApiErrorCodes.BadRequest, `There is no chunk ${n}.`, { field: 'n' });
      }
      const expected = n < row.chunkCount ? row.chunkBytes : row.sizeBytes - (row.chunkCount - 1) * row.chunkBytes;
      if (chunk.size !== expected) {
        throw ncError(400, ApiErrorCodes.BadRequest, `Chunk ${n} must be ${expected} bytes, not ${chunk.size}.`, {
          field: 'body',
        });
      }
      if (failChunk !== undefined && (failChunk.n === undefined || failChunk.n === n)) {
        const { code } = failChunk;
        failChunk = undefined;
        throw code === ApiErrorCodes.NextcloudUnreachable
          ? ncError(502, code, 'Your Nextcloud did not answer. Try again.')
          : ncError(502, code, 'Nextcloud refused the upload.', { status: 500, method: 'PUT' });
      }
      // "An uploading state with progress" (plan 18 §9): the bar moves.
      if (progressDelayMs > 0) {
        for (const fraction of [0.25, 0.5, 0.75]) {
          await wait(progressDelayMs, options?.signal);
          options?.onProgress?.(fraction);
        }
        await wait(progressDelayMs, options?.signal);
      }
      if (options?.signal?.aborted) throw abortError();
      options?.onProgress?.(1);
      row.received.add(n);
      row.chunksDone = row.received.size;
    },

    async finishNextcloudUpload(uploadId, input) {
      await gate();
      requireLinked();
      const row = requireRow(uploadId);
      if (row.state !== 'uploading') {
        throw ncError(409, ApiErrorCodes.Conflict, 'That upload is already finished.');
      }
      if (row.received.size < row.chunkCount) {
        throw ncError(409, ApiErrorCodes.Conflict,
          `${row.chunkCount - row.received.size} of ${row.chunkCount} parts have not arrived yet.`);
      }
      // MOVE .file into place: the file is on Nextcloud from here on.
      fileIds += 1;
      row.fileId = fileIds;
      tree.set(row.path, {
        isDir: false,
        sizeBytes: row.sizeBytes,
        contentType: row.contentType,
        modifiedAt: iso(),
        fileId: fileIds,
      });
      quota.usedBytes += row.sizeBytes;
      return share(row, input ?? {});
    },

    async deleteNextcloudUpload(uploadId) {
      await gate();
      requireLinked();
      const row = requireRow(uploadId);
      if (row.state !== 'uploading') removeRemote(row);
      row.state = 'deleted';
      // A sent row stays, as history; anything else is simply gone.
      if (row.sentAt === null) uploads = uploads.filter((u) => u !== row);
      return wire(row);
    },

    async revokeNextcloudUpload(uploadId) {
      await gate();
      requireLinked();
      const row = requireRow(uploadId);
      if (row.state !== 'staged' && row.state !== 'sent') {
        throw ncError(409, ApiErrorCodes.Conflict, 'That file is not shared.');
      }
      row.shareId = null;
      row.state = 'revoked';
      return wire(row);
    },

    async touchNextcloudUploads(ids) {
      await gate();
      let touched = 0;
      for (const row of uploads) {
        if (!ids.includes(row.id) || row.state !== 'staged') continue;
        row.touchedAt = iso();
        touched += 1;
      }
      return touched;
    },

    async listNextcloudUploads(query, signal) {
      await gate(signal);
      const rows = uploads
        .map(wire)
        .filter((u) => query?.state === undefined || u.state === query.state)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id));
      const start = query?.cursor !== undefined ? Number(query.cursor.replace(/^c/, '')) || 0 : 0;
      const page = rows.slice(start, start + PAGE_SIZE);
      return {
        uploads: page,
        ...(start + PAGE_SIZE < rows.length ? { nextCursor: `c${start + PAGE_SIZE}` } : {}),
      };
    },

    async createNextcloudFolder(path) {
      await gate();
      requireLinked();
      const folder = normalisePath(path);
      if (folder === '') throw ncError(400, ApiErrorCodes.BadRequest, 'Name the folder.', { field: 'path' });
      mkdirAll(folder);
      return { path: folder };
    },

    async cancelNextcloudSave(saveId) {
      await gate();
      if (!/^[A-Za-z0-9_-]{1,64}$/.test(saveId)) {
        throw ncError(400, ApiErrorCodes.BadRequest, 'That save id cannot be used.', { field: 'save_id' });
      }
      // The mock's save stops on its own signal; nothing runs elsewhere.
    },

    async saveAttachments(accountId, messageId, input, onEvent, signal) {
      await gate(signal);
      const ids = input.partIds;
      if (ids.length === 0 || ids.length > 50 || new Set(ids).size !== ids.length) {
        throw ncError(400, ApiErrorCodes.BadRequest, 'Name 1 to 50 different attachments.', { field: 'part_ids' });
      }
      requireLinked();
      const folder = input.folder !== undefined ? normalisePath(input.folder) : (saveFolder ?? saveFolderDefault());
      mkdirAll(folder);
      const base = link.baseUrl ?? MOCK_NEXTCLOUD_BASE_URL;
      const folderUrl = `${base}/index.php/apps/files/?dir=${encodeURIComponent(`/${folder}`)}`;
      onEvent({ type: 'start', folder, folderUrl });
      let saved = 0;
      let failed = 0;
      let quotaFull = false;
      const fail = (partId: string, status: number, code: string, message: string, details?: Record<string, unknown>) => {
        failed += 1;
        onEvent({ type: 'failed', partId, status, error: { code, message, ...(details ? { details } : {}) } });
      };
      for (const partId of ids) {
        if (signal?.aborted) throw abortError();
        const part = findPart?.(accountId, messageId, partId);
        if (part === undefined) {
          fail(partId, 404, ApiErrorCodes.NotFound, 'That attachment is no longer in this message.');
          continue;
        }
        const free = quota.totalBytes - quota.usedBytes;
        if (quotaFull || part.sizeBytes > free) {
          quotaFull = true;
          fail(partId, 413, ApiErrorCodes.NextcloudQuotaExceeded,
            'There is not enough free space on your Nextcloud for this file.', { free_bytes: free });
          continue;
        }
        onEvent({ type: 'progress', partId, phase: 'reading', doneBytes: 0, totalBytes: 0 });
        if (progressDelayMs > 0) await wait(progressDelayMs, signal);
        if (rejected) {
          // Never retried (D3); the stream's fatal `error`.
          withdraw('rejected');
          throw ncError(409, ApiErrorCodes.NextcloudRejected,
            'Nextcloud no longer accepts the link. Link it again in Settings.', { withdrawn: true });
        }
        for (const fraction of progressDelayMs > 0 ? [0.25, 0.5, 0.75, 1] : [1]) {
          if (progressDelayMs > 0) await wait(progressDelayMs, signal);
          onEvent({
            type: 'progress',
            partId,
            phase: 'uploading',
            doneBytes: Math.round(part.sizeBytes * fraction),
            totalBytes: part.sizeBytes,
          });
        }
        // Never overwrite (ADR 0086): `name (2).ext` when the name is taken.
        const wanted = part.filename.replace(/[/\\]/g, '_') || `attachment-${partId}`;
        let name = wanted;
        for (let n = 2; tree.has(folder === '' ? name : `${folder}/${name}`); n += 1) name = withSuffix(wanted, n);
        const path = folder === '' ? name : `${folder}/${name}`;
        fileIds += 1;
        tree.set(path, { isDir: false, sizeBytes: part.sizeBytes, contentType: part.contentType, modifiedAt: iso(), fileId: fileIds });
        quota.usedBytes += part.sizeBytes;
        saved += 1;
        const event: NextcloudSaveEvent = {
          type: 'saved',
          partId,
          name,
          path,
          sizeBytes: part.sizeBytes,
          fileId: fileIds,
          renamed: name !== wanted,
          openUrl: `${base}/index.php/f/${fileIds}`,
        };
        onEvent(event);
      }
      if (saved > 0) saveFolder = folder === saveFolderDefault() ? undefined : folder;
      const done = { type: 'done' as const, saved, failed, folder, folderUrl };
      onEvent(done);
      return done;
    },
  };

  return { api, controls };
}
